import { useState, useEffect } from 'react'
import { k8sResourceService, type K8sResourceListResponse } from '@/services/k8sResourceService'
import SearchableSelector from './SearchableSelector'
import { useTranslation } from 'react-i18next'

interface GitConfigData {
  enabled: boolean
  repoURL: string
  branch: string
  skipSSLVerify: boolean
  credentialsSecret: string
}

interface GitConfigProps {
  value: GitConfigData
  onChange: (config: GitConfigData) => void
  disabled?: boolean
  namespace?: string
  className?: string
}

export default function GitConfig({
  value,
  onChange,
  disabled = false,
  namespace,
  className = ''
}: GitConfigProps) {
  const { t } = useTranslation('common')
  const [isExpanded, setIsExpanded] = useState(false)
  const [secrets, setSecrets] = useState<K8sResourceListResponse | null>(null)
  const [secretNames, setSecretNames] = useState<string[]>([])
  const [secretsLoading, setSecretsLoading] = useState(false)

  // Fetch secrets when expanded and namespace is available
  useEffect(() => {
    const fetchSecrets = async () => {
      if (!isExpanded || !namespace) return

      setSecretsLoading(true)
      try {
        const secretData = await k8sResourceService.getSecrets(namespace)
        setSecrets(secretData)
        const names = k8sResourceService.extractSecretNames(secretData)
        setSecretNames(names)
      } catch (error) {
        console.error('Failed to fetch secrets:', error)
        setSecrets(null)
        setSecretNames([])
      } finally {
        setSecretsLoading(false)
      }
    }

    fetchSecrets()
  }, [isExpanded, namespace])

  const handleInputChange = (field: keyof GitConfigData, newValue: any) => {
    onChange({
      ...value,
      [field]: newValue
    })
  }

  const ChevronDownIcon = () => (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
    </svg>
  )

  const GitIcon = () => (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
    </svg>
  )

  return (
    <div className={`border border-secondary-300 rounded-lg bg-background ${className}`}>
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-secondary-50 transition-colors duration-200"
        onClick={() => !disabled && setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-secondary-100 rounded-full flex items-center justify-center">
              <GitIcon />
            </div>
          </div>
          <div>
            <h3 className="text-sm font-medium text-text-dark">
              Git Repository Configuration
            </h3>
            <p className="text-xs text-text-dark/60">
              {value.enabled ? 'Git integration enabled' : 'Git integration disabled'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Enable/Disable Toggle */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              if (!disabled) {
                handleInputChange('enabled', !value.enabled)
              }
            }}
            disabled={disabled}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-200 ${
              value.enabled ? 'bg-primary' : 'bg-secondary-300'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span
              className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform duration-200 ${
                value.enabled ? 'translate-x-5' : 'translate-x-1'
              }`}
            />
          </button>

          {/* Expand/Collapse Arrow */}
          <div
            className={`text-text-dark/40 transition-transform duration-200 ${
              isExpanded ? 'rotate-180' : ''
            }`}
          >
            <ChevronDownIcon />
          </div>
        </div>
      </div>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-secondary-200 bg-secondary-50/30">
          <div className="pt-4 space-y-4">
            {/* Repository URL */}
            <div>
              <label className="block text-xs font-medium text-text-dark mb-2">
                Repository URL <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                value={value.repoURL}
                onChange={(e) => handleInputChange('repoURL', e.target.value)}
                disabled={disabled || !value.enabled}
                placeholder="https://github.com/user/repo.git"
                className="w-full px-3 py-2 text-sm border border-secondary-300 rounded-lg bg-background text-text-dark placeholder-text-dark/50 focus:ring-2 focus:ring-primary-200 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              />
            </div>

            {/* Branch */}
            <div>
              <label className="block text-xs font-medium text-text-dark mb-2">
                Branch
              </label>
              <input
                type="text"
                value={value.branch}
                onChange={(e) => handleInputChange('branch', e.target.value)}
                disabled={disabled || !value.enabled}
                placeholder="main"
                className="w-full px-3 py-2 text-sm border border-secondary-300 rounded-lg bg-background text-text-dark placeholder-text-dark/50 focus:ring-2 focus:ring-primary-200 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              />
            </div>

            {/* Credentials Secret */}
            <div>
              <SearchableSelector
                title={t('flowConfig.credentialsSecret')}
                value={value.credentialsSecret}
                onChange={(val) => handleInputChange('credentialsSecret', val)}
                options={secretNames}
                placeholder={t('flowConfig.selectASecret')}
                loading={secretsLoading}
                disabled={disabled || !value.enabled}
                emptyMessage="No secrets available in this namespace"
                showCount={false}
              />
              <p className="mt-1 text-xs text-text-dark/60">
                Secret should contain 'username' and 'token' keys
              </p>
            </div>

            {/* Skip SSL Verify */}
            <div className="flex items-center justify-between p-3 bg-background rounded-lg border border-secondary-200">
              <div>
                <label className="text-xs font-medium text-text-dark">
                  Skip SSL Verification
                </label>
                <p className="text-xs text-text-dark/60 mt-1">
                  Skip SSL certificate verification for git operations
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleInputChange('skipSSLVerify', !value.skipSSLVerify)}
                disabled={disabled || !value.enabled}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-200 ${
                  value.skipSSLVerify ? 'bg-primary' : 'bg-secondary-300'
                } ${disabled || !value.enabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span
                  className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform duration-200 ${
                    value.skipSSLVerify ? 'translate-x-5' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}